import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { config } from "~/config"
import { useAccessContext } from "../component/guard/AccessGuard"

type ApiResult = {
  status: "idle" | "loading" | "success" | "error"
  data?: unknown
  error?: string
}

async function callDebugApi(path: string, body?: unknown): Promise<unknown> {
  const url = `${config.apiUrl}/api/v2${path}`
  const res = await fetch(url, {
    method: body !== undefined ? "POST" : "GET",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  return await res.json()
}

type AccountInfoForm = {
  userId: string
}

type DepositForm = {
  userId: string
  requestId: string
  amount: number
}

type ExchangeUrlForm = {
  userId: string
  balance: number
}

const ExchangeUrlLink = ({ result }: { result: ApiResult }) => {
  if (result.status !== "success" || !result.data || typeof result.data !== "object") return null
  const data = result.data as Record<string, unknown>
  if (typeof data["url"] !== "string") return null
  return (
    <div className="mt-2">
      <a href={data["url"]} target="_blank" rel="noopener noreferrer" className="btn btn-link">
        ドットマネー交換画面を開く →
      </a>
    </div>
  )
}

const ResultView = ({ result }: { result: ApiResult }) => {
  if (result.status === "idle") return null
  if (result.status === "loading") return <div className="mt-3"><i className="fas fa-spinner fa-spin" /> 処理中...</div>
  if (result.status === "error") return <div className="mt-3 alert alert-danger">{result.error}</div>
  return (
    <pre className="mt-3 p-3 bg-light border rounded" style={{ maxHeight: "300px", overflow: "auto" }}>
      {JSON.stringify(result.data, null, 2)}
    </pre>
  )
}

const StatusSection = () => {
  const [result, setResult] = useState<ApiResult>({ status: "idle" })

  const checkStatus = async () => {
    setResult({ status: "loading" })
    try {
      const data = await callDebugApi("/debug/dot-money/status")
      setResult({ status: "success", data })
    } catch (e) {
      setResult({ status: "error", error: String(e) })
    }
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">1. 設定状態確認</h5>
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">API キーと接続先の設定状況を確認します。</p>
        <button className="btn btn-outline-primary" onClick={checkStatus} disabled={result.status === "loading"}>
          {result.status === "loading" ? "確認中..." : "設定を確認"}
        </button>
        <ResultView result={result} />
      </div>
    </div>
  )
}

const AccountInfoSection = ({ firebaseUid }: { firebaseUid: string }) => {
  const [result, setResult] = useState<ApiResult>({ status: "idle" })
  const form = useForm<AccountInfoForm>({
    mode: "onBlur",
    defaultValues: { userId: firebaseUid },
  })

  const { isSubmitting, isValid, errors } = form.formState

  const onSubmit = async (value: AccountInfoForm) => {
    setResult({ status: "loading" })
    try {
      const data = await callDebugApi("/debug/dot-money/account-info", { userId: value.userId })
      setResult({ status: "success", data })
    } catch (e) {
      setResult({ status: "error", error: String(e) })
    }
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">3. 口座情報取得</h5>
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">交換画面で口座が作成された後に取得可能です。口座未作成の場合は account_not_found エラーになります。</p>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-3">
            <label className="form-label">User ID</label>
            <input
              className="form-control"
              disabled={isSubmitting}
              {...form.register("userId", { required: "User ID は必須です" })}
            />
            {errors.userId && (
              <div className="text-danger mt-1">{errors.userId.message}</div>
            )}
          </div>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting || !isValid}>
            {isSubmitting ? "取得中..." : "口座情報を取得"}
          </button>
        </form>
        <ResultView result={result} />
      </div>
    </div>
  )
}

const DepositSection = ({ firebaseUid }: { firebaseUid: string }) => {
  const [result, setResult] = useState<ApiResult>({ status: "idle" })
  const form = useForm<DepositForm>({
    mode: "onBlur",
    defaultValues: { userId: firebaseUid, requestId: `poc-${Date.now()}`, amount: 100 },
  })

  const { isSubmitting, isValid, errors } = form.formState

  const onSubmit = async (value: DepositForm) => {
    setResult({ status: "loading" })
    try {
      const data = await callDebugApi("/debug/dot-money/deposit", {
        userId: value.userId,
        requestId: value.requestId,
        amount: Number(value.amount),
      })
      setResult({ status: "success", data })
    } catch (e) {
      setResult({ status: "error", error: String(e) })
    }
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">4. マネー付与（deposit）</h5>
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">テスト用にドットマネーを付与します。本番フローでは使用しません。</p>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="row mb-3">
            <div className="col">
              <label className="form-label">User ID</label>
              <input
                className="form-control"
                disabled={isSubmitting}
                {...form.register("userId", { required: "User ID は必須です" })}
              />
              {errors.userId && (
                <div className="text-danger mt-1">{errors.userId.message}</div>
              )}
            </div>
            <div className="col">
              <label className="form-label">Request ID</label>
              <input
                className="form-control"
                disabled={isSubmitting}
                {...form.register("requestId", { required: "Request ID は必須です" })}
              />
              {errors.requestId && (
                <div className="text-danger mt-1">{errors.requestId.message}</div>
              )}
            </div>
            <div className="col">
              <label className="form-label">Amount</label>
              <input
                className="form-control"
                type="number"
                disabled={isSubmitting}
                {...form.register("amount", { required: "Amount は必須です", min: { value: 1, message: "1以上の値を指定してください" } })}
              />
              {errors.amount && (
                <div className="text-danger mt-1">{errors.amount.message}</div>
              )}
            </div>
          </div>
          <button type="submit" className="btn btn-warning" disabled={isSubmitting || !isValid}>
            {isSubmitting ? "付与中..." : "マネーを付与"}
          </button>
        </form>
        <ResultView result={result} />
      </div>
    </div>
  )
}

const ExchangeUrlSection = ({ firebaseUid }: { firebaseUid: string }) => {
  const [result, setResult] = useState<ApiResult>({ status: "idle" })
  const form = useForm<ExchangeUrlForm>({
    mode: "onBlur",
    defaultValues: { userId: firebaseUid, balance: 1000 },
  })

  const { isSubmitting, isValid, errors } = form.formState

  const onSubmit = async (value: ExchangeUrlForm) => {
    setResult({ status: "loading" })
    try {
      const data = await callDebugApi("/debug/dot-money/exchange-url", {
        userId: value.userId,
        balance: Number(value.balance),
      })
      setResult({ status: "success", data })
    } catch (e) {
      setResult({ status: "error", error: String(e) })
    }
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        <h5 className="mb-0">2. 交換先一覧画面 URL 生成</h5>
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">生成された URL をブラウザで開くとドットマネーの交換画面に遷移します。初回アクセス時にドットマネー口座が作成されます。</p>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="row mb-3">
            <div className="col">
              <label className="form-label">User ID</label>
              <input
                className="form-control"
                disabled={isSubmitting}
                {...form.register("userId", { required: "User ID は必須です" })}
              />
              {errors.userId && (
                <div className="text-danger mt-1">{errors.userId.message}</div>
              )}
            </div>
            <div className="col">
              <label className="form-label">Balance</label>
              <input
                className="form-control"
                type="number"
                disabled={isSubmitting}
                {...form.register("balance", { required: "Balance は必須です", min: { value: 0, message: "0以上の値を指定してください" } })}
              />
              {errors.balance && (
                <div className="text-danger mt-1">{errors.balance.message}</div>
              )}
            </div>
          </div>
          <button type="submit" className="btn btn-success" disabled={isSubmitting || !isValid}>
            {isSubmitting ? "生成中..." : "URL を生成"}
          </button>
        </form>
        <ResultView result={result} />
        <ExchangeUrlLink result={result} />
      </div>
    </div>
  )
}

export const DotMoney = () => {
  const context = useAccessContext()
  const firebaseUid = context.credential.user.uid

  return (
    <div className="container py-4">
      <h2>ドットマネー疎通確認 PoC</h2>
      <p className="text-muted">
        ドットマネー API のサンドボックス環境との疎通確認用ページです。
        本番導線には含まれません。
      </p>
      <div className="alert alert-info mb-4">
        <strong>確認手順:</strong> 1. 設定確認 → 2. 交換URL生成（初回アクセスで口座作成）→ 3. 口座情報取得 → 4. マネー付与
      </div>
      <StatusSection />
      <ExchangeUrlSection firebaseUid={firebaseUid} />
      <AccountInfoSection firebaseUid={firebaseUid} />
      <DepositSection firebaseUid={firebaseUid} />
    </div>
  )
}
